import { captureException, captureMessage, withScope } from "@sentry/react";

// Posted by a worker as the last statement of its module, so the supervisor
// can tell a worker that never started apart from one that failed later.
type WorkerReady = {
	readonly type: "ready";
};

type WorkerSupervisorOptions = {
	// Sentry `source` tag, e.g. "build-worker".
	readonly source: string;
	// Sentry title prefix, e.g. "Build worker".
	readonly label: string;
	// Sentry extra naming where an error was caught, e.g. "buildWorkerErrorContext".
	readonly errorContextExtra: string;
	readonly createWorker: () => Worker;
	// Every message except `ready`.
	readonly onMessage: (event: MessageEvent) => void;
	// Delay before each recreation of a worker that failed before `ready`.
	// Empty means a start failure is only reported.
	readonly startRetryDelaysMs: readonly number[];
	// Sent nothing yet: the caller re-sends whatever the replacement needs.
	readonly onRestart?: (worker: Worker) => void;
};

// A worker whose script fails to fetch or parse fires a plain error event with
// no message and stays dead; a throw while it evaluates fires an ErrorEvent
// with a message. Either way, a failure before `ready` is a start failure and
// the worker is recreated on a schedule. Fixed fingerprints keep one defect as
// one Sentry issue across deploys.
function createWorkerSupervisor({
	source,
	label,
	errorContextExtra,
	createWorker,
	onMessage,
	startRetryDelaysMs,
	onRestart,
}: WorkerSupervisorOptions) {
	let worker: Worker;
	let started = false;
	let failedStarts = 0;
	let terminated = false;
	let retryTimeout: ReturnType<typeof setTimeout> | undefined;

	function reportStartFailure(event: ErrorEvent, msSinceCreated: number) {
		const retryDelayMs = startRetryDelaysMs[failedStarts];
		failedStarts++;
		// Present only when the script loaded and threw while evaluating: a code
		// defect, kept apart from load failures and with its location.
		const evaluationMessage = event.message ? event.message : undefined;
		// eslint-disable-next-line no-console
		console.error(`${label} failed to start`, event.message);
		withScope((scope) => {
			scope.setTag("source", source);
			scope.setLevel(retryDelayMs === undefined ? "error" : "warning");
			scope.setFingerprint(
				evaluationMessage === undefined
					? [source, "failed-to-start"]
					: [source, "failed-to-evaluate", groupingMessage(evaluationMessage)],
			);
			scope.setExtras({
				[errorContextExtra]: "worker-start",
				eventType: event.constructor.name,
				eventMessage: event.message,
				...(evaluationMessage !== undefined && {
					filename: event.filename,
					lineno: event.lineno,
					colno: event.colno,
				}),
				failedStarts,
				msSinceCreated,
				willRetry: retryDelayMs !== undefined,
				online: navigator.onLine,
				deviceMemory: (navigator as Navigator & { deviceMemory?: number })
					.deviceMemory,
			});
			// Sentry's dedupe drops an event identical to the previous one (same
			// message, fingerprint and stack), which would swallow the final
			// error-level report after a run of identical retry warnings.
			const title =
				retryDelayMs === undefined && failedStarts > 1
					? `${label} failed to start after ${failedStarts} attempts`
					: `${label} failed to start`;
			captureException(
				new Error(
					evaluationMessage === undefined
						? title
						: `${title}: ${evaluationMessage}`,
				),
			);
		});
		return retryDelayMs;
	}

	// A backstop: the worker forwards its own runtime errors and cancels them,
	// so this only sees what slips past its handler. The browser never passes
	// the original error object across, so group by message, not stack.
	function reportUncaught(event: ErrorEvent, msSinceCreated: number) {
		// eslint-disable-next-line no-console
		console.error(`${label} uncaught error`, event.message);
		withScope((scope) => {
			scope.setTag("source", source);
			scope.setFingerprint(
				event.message
					? [source, "worker-uncaught", groupingMessage(event.message)]
					: [source, "worker-uncaught"],
			);
			scope.setExtras({
				[errorContextExtra]: "worker-uncaught",
				msSinceCreated,
				filename: event.filename,
				lineno: event.lineno,
				colno: event.colno,
			});
			captureException(new Error(event.message || `${label} error`));
		});
	}

	function start() {
		const createdAt = performance.now();
		started = false;
		// Set before reporting, so a second error from the same attempt can't
		// schedule a second replacement.
		let failed = false;
		const created = createWorker();
		worker = created;
		// A failed, replaced or terminated worker can still fire late; ignore it.
		function isCurrent() {
			return !terminated && !failed && worker === created;
		}
		created.addEventListener("message", (event: MessageEvent) => {
			if (!isCurrent()) {
				return;
			}
			const data: unknown = event.data;
			if (isWorkerReady(data)) {
				started = true;
				if (failedStarts > 0) {
					captureMessage(`${label} started after retry`, {
						level: "info",
						tags: { source },
						extra: { failedStarts },
					});
				}
				return;
			}
			onMessage(event);
		});
		created.onerror = (event: ErrorEvent): void => {
			// Reported here (or deliberately ignored), so stop it reaching the
			// page's global error handler as a second report.
			event.preventDefault();
			if (!isCurrent()) {
				return;
			}
			const msSinceCreated = Math.round(performance.now() - createdAt);
			if (started) {
				reportUncaught(event, msSinceCreated);
				return;
			}
			failed = true;
			created.terminate();
			const retryDelayMs = reportStartFailure(event, msSinceCreated);
			if (retryDelayMs === undefined) {
				return;
			}
			retryTimeout = setTimeout(() => {
				retryTimeout = undefined;
				if (!terminated) {
					start();
					onRestart?.(worker);
				}
			}, retryDelayMs);
		};
	}

	start();

	return {
		postMessage(data: unknown, transfer?: Transferable[]) {
			// A no-op on a worker that failed to start; the replacement is
			// re-sent what it needs by onRestart.
			if (transfer === undefined) {
				worker.postMessage(data);
			} else {
				worker.postMessage(data, transfer);
			}
		},
		terminate() {
			terminated = true;
			clearTimeout(retryTimeout);
			worker.terminate();
		},
	};
}

// Chromium prefixes worker error messages with "Uncaught "; Safari and Firefox
// don't. Without this one defect becomes an issue per browser.
function groupingMessage(message: string) {
	return message.replace(/^Uncaught /, "");
}

function isWorkerReady(data: unknown): data is WorkerReady {
	return (
		typeof data === "object" &&
		data !== null &&
		(data as { type?: unknown }).type === "ready"
	);
}

type WorkerSupervisor = ReturnType<typeof createWorkerSupervisor>;

export type { WorkerReady, WorkerSupervisor };
export { createWorkerSupervisor };
